// IndexedDB rather than localStorage; base64 avatars overflow the 5MB cap.
// Mobile counterpart uses expo-file-system.

export type ApprovedAvatar = {
  id: string;
  bytes: string; // base64
};

export type ApprovedSet = {
  approvedBios: string[];
  approvedAvatars: ApprovedAvatar[];
};

const DB_NAME = 'factiii.verification';
const STORE_NAME = 'approved';
const DB_VERSION = 1;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE_NAME);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const req = fn(tx.objectStore(STORE_NAME));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB op failed'));
  });
}

export async function saveApproved(
  userId: number,
  set: ApprovedSet
): Promise<void> {
  await withStore('readwrite', (s) => s.put(set, userId));
}

export async function loadApproved(
  userId: number
): Promise<ApprovedSet | null> {
  const result = await withStore<ApprovedSet | undefined>('readonly', (s) =>
    s.get(userId)
  );
  return result ?? null;
}

export async function clearApproved(userId: number): Promise<void> {
  await withStore('readwrite', (s) => s.delete(userId));
}
